<template>
	<Teleport to="body">
		<Accessible
			name="standardmodal"
			trapped
			focusFirstItem
			autoFocus
			:wait="250"
			:tabOrder="[
				{ css: '.modal-close', clickOnEnter: true },
				...tabOrder,
				{ css: '.modal-footer-right .pronto-btn.default', clickOnEnter: true },
				{ css: '.modal-footer-right .pronto-btn.primary', clickOnEnter: true },
			]"
			:class="$attrs.class"
		>
			<div
				v-bind="scopedStyleAttrs"
				class="standard-modal"
				:class="rootClasses"
				ref="container"
			>
				<Alert ref="alert">
					<template #alerts="{ alerts }">
						<div class="modal-trigger" @click="openModal">
							<slot name="trigger" />
						</div>
						<Modal
							v-if="isOpen"
							:onBeforeClose="onBeforeClose"
							@close="onClickClose"
							ref="modal"
						>
							<div class="modal-container">
								<div v-if="loading" class="loading-overlay">
									<IosSpinner />
								</div>
								<slot name="header">
									<div class="modal-header">
										<div class="modal-header-left">
											<slot name="title">
												<h1
													class="modal-title"
													data-cy="modal-title"
													v-text="title"
												/>
											</slot>
										</div>
										<div class="modal-header-right">
											<slot name="header-right">
												<CloseIcon
													hover
													@click="onClickClose"
													class="modal-close"
													:aria-label="$t('common_button_close')"
													tabindex="0"
													data-cy="modal-close"
													type="regular"
													role="button"
												/>
											</slot>
										</div>
									</div>
								</slot>
								<div
									class="modal-body"
									ref="body"
									data-cy="modal-body"
									@scroll="$emit('body-scroll', $event)"
								>
									<slot />
								</div>
								<div class="modal-footer">
									<slot name="footer-extra" />
									<div class="alert-container">
										<AlertList :alerts="alerts.slice(0, 1)" theme="modal" />
									</div>
									<div class="modal-footer-content">
										<slot name="footer">
											<div class="modal-footer-left">
												<slot name="footer-left" />
											</div>
											<div class="modal-footer-right">
												<slot name="footer-right">
													<slot name="cancel">
														<Button
															v-bind="cancelProps"
															v-if="!hideCancel"
															@click="onCancel"
															:dark="dark"
															data-cy="modal-cancel"
														>
															{{ cancelText || $t('common_button_cancel') }}
														</Button>
													</slot>
													<slot name="confirm">
														<Button
															v-bind="confirmProps"
															primary
															:dark="dark"
															v-if="!hideConfirm"
															@click="onConfirm"
															data-cy="modal-confirm"
														>
															{{ confirmText || $t('common_button_save') }}
														</Button>
													</slot>
												</slot>
											</div>
										</slot>
									</div>
								</div>
							</div>
						</Modal>
					</template>
				</Alert>
			</div>
		</Accessible>
	</Teleport>
</template>

<script>
import IosSpinner from '@/components/IosSpinner'
import { Modal, Button } from '@/components/common'
import Accessible from '@/components/common/Accessible'
import Alert, { AlertList, alertTypes } from './Alert'
import { CloseIcon } from '@/icons'
import debounce from 'debounce'
import { sleep } from '@/helpers/utils'
import { setColorSchemeForElement } from '@/helpers/colors'
import { colorSchemes } from '@/helpers/constants'

export default {
	name: 'StandardModal',
	inheritAttrs: false,
	components: {
		Modal,
		Button,
		CloseIcon,
		IosSpinner,
		Alert,
		AlertList,
		Accessible,
	},
	props: {
		open: { type: Boolean, default: null },
		large: { type: Boolean, default: null },
		medium: { type: Boolean, default: null },
		small: { type: Boolean, default: null },
		dark: { type: Boolean, default: null },
		title: String,
		loading: { type: Boolean, default: null },
		hideCancel: { type: Boolean, default: null },
		hideConfirm: { type: Boolean, default: null },
		confirmText: String,
		cancelText: String,
		confirmProps: Object,
		cancelProps: Object,
		onBeforeClose: Function,
		tabOrder: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			isOpen: this.open,
		}
	},
	//TODO: the events will not show in the $attrs IF this line is commented out.  Remove if testing goes well, but if bugs are found related to this component you might uncomment this line.
	// emits: ['body-scroll', 'confirm', 'cancel', 'close', 'open', 'body-resize'],
	inheritAttrs: false,

	async mounted() {
		await this.$nextTick()

		if (this.dark) {
			setColorSchemeForElement(this.$el, colorSchemes.DARK)
		}

		const { alertMethods } = this.$refs.alert || {}
		if (alertMethods) {
			// alias the alert methods onto the modal instance
			this.$alert = {}
			Object.entries(alertMethods)
				.filter(([key]) => key !== alertTypes.API)
				.forEach(([key, func]) => {
					this.$alert[key] = (text, data = {}) =>
						func(text, { closeable: false, autoClose: false, ...data })
				})
			this.$alert[alertTypes.API] = (e, data = {}) =>
				alertMethods[alertTypes.API](e, {
					closeable: false,
					autoClose: false,
					...data,
				})
		}

		if (this.$attrs.onBodyResize) {
			await sleep(100)
			if (this.$refs.body) {
				this.onBodyResizeDebounced = debounce(this.onBodyResize, 25, true)
				this.bodyResizeObserver = new ResizeObserver(this.onBodyResizeDebounced)
				this.bodyResizeObserver.observe(this.$refs.body)
			}
		}
	},

	watch: {
		open(open) {
			open ? this.openModal() : this.closeModal()
		},
	},

	computed: {
		scopedStyleAttrs() {
			const scopeId = this.getScopeId(this.$parent)
			return scopeId ? { [scopeId]: '' } : {}
		},
		rootClasses() {
			const classes = {
				small: this.small,
				medium: this.medium,
				large: this.large,
			}
			if (this.$attrs.class) {
				classes[this.$attrs.class] = true
			}
			return classes
		},
	},

	methods: {
		getScopeId(vm, defaultValue = null) {
			return vm ? vm.$options._scopeId || defaultValue : defaultValue
		},

		onConfirm(e) {
			this.$emit('confirm', e)
		},

		onCancel(e) {
			if (this.$attrs.onCancel) {
				this.$emit('cancel', e)
			} else {
				this.closeModal()
			}
		},
		onClickClose(e) {
			if (this.$attrs.onClose) {
				// if parent is listening for 'close' event, only
				// emit that, rather than 'cancel'
				this.$emit('close', e)
			} else {
				// otherwise, parent doesn't care about 'close' event specifically
				// so we'll just treat 'close' and 'cancel' as the same thing
				this.onCancel(e)
			}
		},
		async openModal() {
			this.isOpen = true
			await this.$nextTick()
			this.$emit('open')
		},
		closeModal() {
			this.isOpen = false
		},
		toggleModal() {
			this.isOpen = !this.isOpen
		},
		onBodyResize() {
			requestAnimationFrame(() => {
				if (this.$refs.body) {
					this.$emit('body-resize', this.$refs.body.getBoundingClientRect())
				}
			})
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

// vars
$border: 1px solid var(--neutral-200);
$header-height: 87px;
$small-width: 500px;
$small-height: 400px;
$medium-width: 600px;
$medium-height: 700px;
$large-width: 1000px;
$large-height: 800px;

.standard-modal {
	:deep(.modal-content) {
		max-width: 95vw;
		max-height: 95vh;
		width: $medium-width;
		height: $medium-height;
	}
	&.small {
		:deep(.modal-content) {
			width: $small-width;
			height: $small-height;
		}
	}
	&.medium {
		:deep(.modal-content) {
			width: $medium-width;
			height: $medium-height;
		}
	}
	&.large {
		:deep(.modal-content) {
			width: $large-width;
			height: $large-height;
		}
	}
}
.modal-container {
	position: relative;
	@include flex-column;
	align-items: stretch;
	height: 100%;
	.modal-header {
		@include flex-spread;
		flex-shrink: 0;
		height: $header-height;
		border-bottom: $border;
		padding: 0 24px;
		.modal-header-left {
			width: 100%;
			max-width: calc(100% - 50px);
			flex-grow: 1;
			.modal-title {
				color: var(--neutral-800);
				max-width: 100%;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				font-size: 26px;
				font-family: Avenir-Black;
			}
		}
		.modal-header-right {
			margin-left: 8px;
			&:hover {
				.modal-close {
					color: darken($text-gray, 10%);
				}
			}
			.modal-close {
				font-size: 32px;
				cursor: pointer;
				z-index: 1;
				outline: 0;
				margin: 4px 0 -6px 0;
				color: var(--neutral-400);
			}
		}
	}
	.modal-body {
		flex-grow: 1;
		overflow-x: hidden;
		overflow-y: auto;
		height: 100%;
	}
	.modal-footer {
		@include flex-column;
		justify-content: center;
		flex-shrink: 0;
		border-top: $border;
		padding: 20px 24px;
		background: var(--neutral-100);
		transition: height 150ms ease-in-out;
		.modal-footer-content {
			@include flex-spread;
		}
		:deep(.alert-item) {
			width: 100%;
			font-family: Avenir-Heavy;
			box-shadow: none;
			margin-bottom: 16px;
			height: 40px;
			padding: 0 8px;
			&.error {
				border: 1px solid var(--red-200);
				color: var(--red-200);
				> .alert-icon {
					--color: #{$error-red};
				}
			}
			&.success {
				border: 1px solid var(--teal-300);
				color: var(--teal-300);
				> .alert-icon {
					--color: var(--teal-300);
				}
			}
			> .alert-icon {
				margin-right: 12px;
			}
		}
	}
	.loading-overlay {
		@include absolute-cover;
		@include flex-center;
		background: rgba(255, 255, 255, 0.5);
		z-index: 1;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.standard-modal {
		.modal-container {
			.modal-header {
				border-bottom-color: var(--neutral-300);
			}
			.modal-footer {
				background: var(--neutral-0);
				border-top-color: var(--neutral-300);
				.alert-item {
					background: var(--neutral-100);
				}
			}
		}
	}
}
</style>
